/**
 * Deployment profiles — the deployment TARGETS an app can be published to.
 *
 * This used to live in Settings → Deployment, which put an advanced,
 * admin-level operation next to everyday preferences and made it look like
 * something every user configures. Deploying is neither required for, nor
 * related to, the live preview: the preview runs against a platform-owned
 * database that has no profile. Profiles belong with the Deploy action, so
 * they live here, on the Deploy page, next to the deploy that consumes them.
 *
 * Self-contained: owns its own state, loads its own profiles, and is safe to
 * mount anywhere on the Deploy page.
 */
import { useState, useEffect } from 'react'
import {
  listDeploymentProfiles, createDeploymentProfile, updateDeploymentProfile,
  deleteDeploymentProfile, activateDeploymentProfile,
} from '@/api/settings'
import { testDbConnection } from '@/api/workbench'
import type { DeploymentProfile } from '@/types'
import toast from 'react-hot-toast'

const DEPLOYMENT_TARGETS = [
  { value: 'twasta_cloud', label: 'Twasta Cloud' },
  { value: 'oracle_database', label: 'Oracle Database' },
  { value: 'postgresql_database', label: 'PostgreSQL Database' },
  { value: 'mysql_database', label: 'MySQL Database' },
]

const DATABASE_OPTIONS = [
  { value: 'oracle', label: 'Oracle' },
  { value: 'postgresql', label: 'PostgreSQL' },
  { value: 'mysql', label: 'MySQL' },
]

const TARGET_TO_DB: Record<string, string> = {
  twasta_cloud: 'oracle',
  oracle_database: 'oracle',
  postgresql_database: 'postgresql',
  mysql_database: 'mysql',
}

const DEFAULT_PORTS: Record<string, string> = {
  oracle: '1521',
  postgresql: '5432',
  mysql: '3306',
}

export default function DeploymentProfilesPanel({ onChanged }: {
  /** Fired after a profile is created, updated, deleted or activated, so a
   *  host page can refresh its own copy of the profile list. */
  onChanged?: () => void
} = {}) {
  const [profiles, setProfiles] = useState<DeploymentProfile[]>([])
  const [editingProfile, setEditingProfile] = useState<Partial<DeploymentProfile> | null>(null)
  const [editingProfileId, setEditingProfileId] = useState<number | null>(null)
  const [savingProfile, setSavingProfile] = useState(false)
  const [deletingProfileId, setDeletingProfileId] = useState<number | null>(null)
  const [testingDb, setTestingDb] = useState(false)

  useEffect(() => { loadProfiles() }, [])

  const loadProfiles = async () => {
    try {
      setProfiles(await listDeploymentProfiles())
    } catch {
      // silent — the panel simply shows the empty state
    }
  }

  const handleNewProfile = () => {
    setEditingProfileId(null)
    setEditingProfile({
      profile_name: '',
      deployment_target: 'twasta_cloud',
      application_database: 'oracle',
      db_host: '',
      db_port: '',
      db_username: '',
      db_password: '',
      db_name: '',
      oracle_connect_type: 'service_name',
    })
  }

  const handleEditProfile = (p: DeploymentProfile) => {
    setEditingProfileId(p.id)
    setEditingProfile({ ...p })
  }

  const handleCancelEditProfile = () => {
    setEditingProfile(null)
    setEditingProfileId(null)
  }

  const handleSaveProfile = async () => {
    if (!editingProfile) return
    setSavingProfile(true)
    try {
      if (editingProfileId) {
        await updateDeploymentProfile(editingProfileId, editingProfile)
        toast.success('Profile updated')
      } else {
        await createDeploymentProfile(editingProfile)
        toast.success('Profile created')
      }
      await loadProfiles()
      onChanged?.()
      setEditingProfile(null)
      setEditingProfileId(null)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to save profile'
      toast.error(msg)
    } finally {
      setSavingProfile(false)
    }
  }

  const handleDeleteProfile = async (id: number) => {
    if (!window.confirm('Delete this deployment profile?')) return
    setDeletingProfileId(id)
    try {
      await deleteDeploymentProfile(id)
      toast.success('Profile deleted')
      await loadProfiles()
      onChanged?.()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to delete profile'
      toast.error(msg)
    } finally {
      setDeletingProfileId(null)
    }
  }

  const handleActivateProfile = async (id: number) => {
    try {
      await activateDeploymentProfile(id)
      toast.success('Profile activated')
      await loadProfiles()
      onChanged?.()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to activate profile'
      toast.error(msg)
    }
  }

  const updateProfileField = <K extends keyof DeploymentProfile>(key: K, value: DeploymentProfile[K]) => {
    setEditingProfile((prev) => {
      if (!prev) return prev
      const updated = { ...prev, [key]: value }
      if (key === 'deployment_target') {
        const autoDb = TARGET_TO_DB[value as string]
        if (autoDb) {
          updated.application_database = autoDb
          updated.db_port = DEFAULT_PORTS[autoDb] || ''
        }
      }
      return updated
    })
  }

  const handleTestProfileDbConnection = async () => {
    if (!editingProfile) return
    const dbType = editingProfile.application_database || 'oracle'
    if (!editingProfile.db_host) {
      toast.error('Enter a host first')
      return
    }
    if (!editingProfile.db_name) {
      toast.error('Enter a database/service name first')
      return
    }
    setTestingDb(true)
    try {
      const result = await testDbConnection({
        db_type: dbType,
        host: editingProfile.db_host || '',
        port: editingProfile.db_port || '',
        username: editingProfile.db_username || '',
        password: editingProfile.db_password || '',
        database: editingProfile.db_name || '',
        oracle_connect_type: editingProfile.oracle_connect_type || 'service_name',
      })
      if (result.success) {
        toast.success(result.message || 'Connection successful!')
      } else {
        toast.error(result.error || 'Connection failed')
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Connection test failed'
      toast.error(msg)
    } finally {
      setTestingDb(false)
    }
  }

  return (
    <>
      {editingProfile ? (
        /* ---- Profile Edit/Create Form ---- */
        <section style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '1.5rem',
          marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem' }}>
            <span className="material-icons" style={{ fontSize: 21, color: 'var(--color-accent)' }}>
              {editingProfileId ? 'edit' : 'add_circle'}
            </span>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600 }}>
              {editingProfileId ? 'Edit Profile' : 'New Deployment Profile'}
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label htmlFor="profile-name">Profile Name</label>
              <input
                id="profile-name"
                type="text"
                value={editingProfile.profile_name || ''}
                onChange={(e) => updateProfileField('profile_name', e.target.value)}
                placeholder="e.g. Development, Production"
              />
            </div>

            <div>
              <label htmlFor="profile-target">Target Environment</label>
              <select
                id="profile-target"
                value={editingProfile.deployment_target || 'twasta_cloud'}
                onChange={(e) => updateProfileField('deployment_target', e.target.value)}
              >
                {DEPLOYMENT_TARGETS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {editingProfile.deployment_target !== 'twasta_cloud' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                  <div>
                    <label htmlFor="profile-db-host">Host</label>
                    <input
                      id="profile-db-host"
                      type="text"
                      value={editingProfile.db_host || ''}
                      onChange={(e) => updateProfileField('db_host', e.target.value)}
                      placeholder="e.g. localhost or db.example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="profile-db-port">Port</label>
                    <input
                      id="profile-db-port"
                      type="text"
                      value={editingProfile.db_port || ''}
                      onChange={(e) => updateProfileField('db_port', e.target.value)}
                      placeholder={DEFAULT_PORTS[editingProfile.application_database || 'oracle'] || '1521'}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="profile-db-username">Username</label>
                  <input
                    id="profile-db-username"
                    type="text"
                    value={editingProfile.db_username || ''}
                    onChange={(e) => updateProfileField('db_username', e.target.value)}
                    placeholder="Database username"
                    autoComplete="off"
                    data-form-type="other"
                    data-lpignore="true"
                  />
                </div>

                <div>
                  <label htmlFor="profile-db-password">Password</label>
                  <input
                    id="profile-db-password"
                    type="password"
                    value={editingProfile.db_password || ''}
                    onChange={(e) => updateProfileField('db_password', e.target.value)}
                    placeholder="Database password"
                    autoComplete="new-password"
                    data-form-type="other"
                    data-lpignore="true"
                  />
                </div>

                {editingProfile.application_database === 'oracle' && (
                  <div>
                    <label htmlFor="profile-oracle-connect-type">Connect Using</label>
                    <select
                      id="profile-oracle-connect-type"
                      value={editingProfile.oracle_connect_type || 'service_name'}
                      onChange={(e) => updateProfileField('oracle_connect_type', e.target.value)}
                    >
                      <option value="service_name">Service Name</option>
                      <option value="sid">SID</option>
                    </select>
                  </div>
                )}

                <div>
                  <label htmlFor="profile-db-name">
                    {editingProfile.application_database === 'oracle'
                      ? (editingProfile.oracle_connect_type === 'sid' ? 'SID' : 'Service Name')
                      : 'Database Name'}
                  </label>
                  <input
                    id="profile-db-name"
                    type="text"
                    value={editingProfile.db_name || ''}
                    onChange={(e) => updateProfileField('db_name', e.target.value)}
                    placeholder={editingProfile.application_database === 'oracle'
                      ? (editingProfile.oracle_connect_type === 'sid' ? 'e.g. ORCL' : 'e.g. ORCL or pdb1.example.com')
                      : 'e.g. myapp_db'}
                  />
                </div>
              </>
            )}

            <div>
              <label htmlFor="profile-app-db">Application Database</label>
              <select
                id="profile-app-db"
                value={editingProfile.application_database || 'oracle'}
                disabled
                style={{ opacity: 0.7, cursor: 'not-allowed' }}
              >
                {DATABASE_OPTIONS.map((db) => (
                  <option key={db.value} value={db.value}>{db.label}</option>
                ))}
              </select>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, display: 'block' }}>
                Automatically set based on deployment target.
              </span>
            </div>

            {/* ---- App Server Configuration ---- */}
            {editingProfile.deployment_target !== 'twasta_cloud' && (
              <>
                <div style={{
                  borderTop: '1px solid var(--color-border)',
                  paddingTop: '1rem',
                  marginTop: '0.5rem',
                }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>
                    App Server Configuration
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <label htmlFor="profile-app-server-type">App Server Type</label>
                      <div style={{ display: 'flex', gap: '1rem', marginTop: 4 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.875rem' }}>
                          <input
                            type="radio"
                            name="app_server_type"
                            value="local"
                            checked={(editingProfile.app_server_type || 'local') === 'local'}
                            onChange={() => updateProfileField('app_server_type', 'local')}
                          />
                          Local
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.875rem' }}>
                          <input
                            type="radio"
                            name="app_server_type"
                            value="remote"
                            checked={editingProfile.app_server_type === 'remote'}
                            onChange={() => updateProfileField('app_server_type', 'remote')}
                          />
                          Remote
                        </label>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="profile-metadata-path">Metadata Path</label>
                      <input
                        id="profile-metadata-path"
                        type="text"
                        value={editingProfile.metadata_path || ''}
                        onChange={(e) => updateProfileField('metadata_path', e.target.value)}
                        placeholder="e.g. /wildfly/server/default/deploy/ibase.ear/metadata/"
                      />
                      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4, display: 'block' }}>
                        Directory where XML metadata files are deployed.
                      </span>
                    </div>

                    {editingProfile.app_server_type === 'remote' && (
                      <>
                        <div>
                          <label htmlFor="profile-app-server-url">Server URL</label>
                          <input
                            id="profile-app-server-url"
                            type="text"
                            value={editingProfile.app_server_url || ''}
                            onChange={(e) => updateProfileField('app_server_url', e.target.value)}
                            placeholder="e.g. https://appserver.example.com/deploy"
                          />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div>
                            <label htmlFor="profile-app-id">App ID</label>
                            <input
                              id="profile-app-id"
                              type="text"
                              value={editingProfile.app_server_app_id || ''}
                              onChange={(e) => updateProfileField('app_server_app_id', e.target.value)}
                              placeholder="Application ID"
                            />
                          </div>
                          <div>
                            <label htmlFor="profile-auth-token">Auth Token</label>
                            <input
                              id="profile-auth-token"
                              type="password"
                              value={editingProfile.app_server_auth_token || ''}
                              onChange={(e) => updateProfileField('app_server_auth_token', e.target.value)}
                              placeholder="Server auth token"
                              autoComplete="new-password"
                              data-form-type="other"
                              data-lpignore="true"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="profile-cache-clear-url">Cache Clear URL (optional)</label>
                          <input
                            id="profile-cache-clear-url"
                            type="text"
                            value={editingProfile.cache_clear_url || ''}
                            onChange={(e) => updateProfileField('cache_clear_url', e.target.value)}
                            placeholder="e.g. https://appserver.example.com/clear-cache"
                          />
                        </div>
                      </>
                    )}

                    {/* Backup settings */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        id="profile-backup-enabled"
                        type="checkbox"
                        checked={editingProfile.backup_enabled !== false}
                        onChange={(e) => updateProfileField('backup_enabled', e.target.checked)}
                        style={{ width: 16, height: 16 }}
                      />
                      <label htmlFor="profile-backup-enabled" style={{ margin: 0, cursor: 'pointer' }}>
                        Enable file backup before deployment
                      </label>
                    </div>

                    {editingProfile.backup_enabled !== false && (
                      <div>
                        <label htmlFor="profile-backup-path">Backup Path</label>
                        <input
                          id="profile-backup-path"
                          type="text"
                          value={editingProfile.backup_path || ''}
                          onChange={(e) => updateProfileField('backup_path', e.target.value)}
                          placeholder="e.g. /var/backups/twasta/"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ---- Website publishing (SITE deployments) ---- */}
            <div style={{
              borderTop: '1px solid var(--color-border)',
              paddingTop: '1rem',
              marginTop: '0.5rem',
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Website publishing
              </h3>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '1rem' }}>
                Where a published external website is written. A local folder is served on the site&apos;s
                own domain by this instance; an upload URL receives each version as a tar.gz.
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label htmlFor="profile-site-path">Publish folder</label>
                  <input
                    id="profile-site-path"
                    type="text"
                    value={editingProfile.site_artifact_path || ''}
                    onChange={(e) => updateProfileField('site_artifact_path', e.target.value)}
                    placeholder="e.g. /var/twasta/sites"
                  />
                </div>
                <div>
                  <label htmlFor="profile-site-url">Upload URL (instead of a folder)</label>
                  <input
                    id="profile-site-url"
                    type="text"
                    value={editingProfile.site_upload_url || ''}
                    onChange={(e) => updateProfileField('site_upload_url', e.target.value)}
                    placeholder="e.g. https://static.example.com/upload"
                  />
                </div>
                {!!(editingProfile.site_upload_url || '').trim() && (
                  <div>
                    <label htmlFor="profile-site-token">Upload token</label>
                    <input
                      id="profile-site-token"
                      type="password"
                      autoComplete="new-password"
                      value={editingProfile.site_upload_auth_token || ''}
                      onChange={(e) => updateProfileField('site_upload_auth_token', e.target.value)}
                      placeholder="Sent as a Bearer token"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ---- InsightDB (business areas imported by reverse deploy) ---- */}
          <div style={{
            borderTop: '1px solid var(--color-border)',
            paddingTop: '1rem',
            marginTop: '1rem',
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              InsightDB
            </h3>
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '1rem' }}>
              The InsightDB server holding this install&apos;s Assistant datasets. Importing a deployed
              application also brings its business areas in as Business Areas, with their columns read
              from the database above.
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label htmlFor="profile-insightdb-url">Server URL</label>
                <input
                  id="profile-insightdb-url"
                  type="text"
                  value={editingProfile.insightdb_url || ''}
                  onChange={(e) => updateProfileField('insightdb_url', e.target.value)}
                  placeholder="e.g. http://std.insightdbchat.proteusvision.com:8000"
                />
              </div>
              {!!(editingProfile.insightdb_url || '').trim() && (
                <>
                  <div>
                    <label htmlFor="profile-insightdb-key">API key</label>
                    <input
                      id="profile-insightdb-key"
                      type="password"
                      autoComplete="new-password"
                      value={editingProfile.insightdb_api_key || ''}
                      onChange={(e) => updateProfileField('insightdb_api_key', e.target.value)}
                      placeholder="Created in InsightDB under API Keys"
                    />
                  </div>
                  <div>
                    <label htmlFor="profile-insightdb-app">InsightDB application (optional)</label>
                    <input
                      id="profile-insightdb-app"
                      type="text"
                      value={editingProfile.insightdb_application || ''}
                      onChange={(e) => updateProfileField('insightdb_application', e.target.value)}
                      placeholder="e.g. PTPLVision — blank imports every business area the key can see"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
            {editingProfile.deployment_target !== 'twasta_cloud' && (
              <button
                className="btn btn-secondary"
                onClick={handleTestProfileDbConnection}
                disabled={testingDb}
              >
                {testingDb ? (
                  <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Testing...</>
                ) : (
                  <><span className="material-icons" style={{ fontSize: 17 }}>wifi_tethering</span> Test Connection</>
                )}
              </button>
            )}
            <button className="btn btn-secondary" onClick={handleCancelEditProfile}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveProfile} disabled={savingProfile}>
              {savingProfile ? (
                <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Saving...</>
              ) : (
                <><span className="material-icons" style={{ fontSize: 17 }}>save</span> Save Profile</>
              )}
            </button>
          </div>
        </section>
      ) : (
        /* ---- Profile List View ---- */
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: 0 }}>
              Active profile determines the deployment target and database for code generation.
            </p>
            <button className="btn btn-primary btn-sm" onClick={handleNewProfile}>
              <span className="material-icons" style={{ fontSize: 17 }}>add</span>
              New Profile
            </button>
          </div>

          {profiles.length === 0 ? (
            <section style={{
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '2.5rem 1.5rem',
              textAlign: 'center',
              marginBottom: '1.5rem',
            }}>
              <span className="material-icons" style={{ fontSize: 41, color: 'var(--color-text-secondary)', opacity: 0.5 }}>cloud_off</span>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', marginTop: '0.75rem' }}>
                No deployment profiles yet. Create one to configure your deployment target and database connection.
              </p>
            </section>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {profiles.map((p) => {
                const targetLabel = DEPLOYMENT_TARGETS.find((t) => t.value === p.deployment_target)?.label || p.deployment_target
                const dbSummary = p.deployment_target !== 'twasta_cloud' && p.db_host
                  ? `${p.db_host}${p.db_port ? ':' + p.db_port : ''}${p.db_name ? '/' + p.db_name : ''}`
                  : ''
                return (
                  <section
                    key={p.id}
                    style={{
                      background: 'var(--color-bg-secondary)',
                      border: `1.5px solid ${p.is_active ? 'var(--color-accent)' : 'var(--color-border)'}`,
                      borderRadius: 'var(--radius-lg)',
                      padding: '1rem 1.25rem',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span className="material-icons" style={{ fontSize: 21, color: p.is_active ? 'var(--color-accent)' : 'var(--color-text-secondary)' }}>
                          {p.is_active ? 'check_circle' : 'radio_button_unchecked'}
                        </span>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontWeight: 600, fontSize: '1rem' }}>{p.profile_name}</span>
                            {p.is_active && (
                              <span style={{
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                padding: '2px 8px',
                                borderRadius: '999px',
                                background: 'var(--color-accent)',
                                color: '#fff',
                              }}>
                                Active
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                            {targetLabel}
                            {dbSummary && <span style={{ marginLeft: 8, opacity: 0.7 }}>{dbSummary}</span>}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '0.375rem' }}>
                        {!p.is_active && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleActivateProfile(p.id)}
                            title="Activate"
                            style={{ padding: '4px 8px' }}
                          >
                            <span className="material-icons" style={{ fontSize: 19 }}>power_settings_new</span>
                          </button>
                        )}
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleEditProfile(p)}
                          title="Edit"
                          style={{ padding: '4px 8px' }}
                        >
                          <span className="material-icons" style={{ fontSize: 19 }}>edit</span>
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleDeleteProfile(p.id)}
                          disabled={deletingProfileId === p.id}
                          title="Delete"
                          style={{ padding: '4px 8px', color: 'var(--color-error, #e74c3c)' }}
                        >
                          {deletingProfileId === p.id ? (
                            <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                          ) : (
                            <span className="material-icons" style={{ fontSize: 19 }}>delete</span>
                          )}
                        </button>
                      </div>
                    </div>
                  </section>
                )
              })}
            </div>
          )}
        </>
      )}
    </>
  )
}
